import React, { useState } from 'react'
import { X, LifeBuoy, Send, CheckCircle } from 'lucide-react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import {
  submitSupportTicket,
  type SupportTicketCategory,
  type SupportTicketPriority,
} from '../services/api'
import './SupportTicketModal.css'

interface SupportTicketModalProps {
  open: boolean
  onClose: () => void
}

const CATEGORIES: { value: SupportTicketCategory; label: string; emoji: string }[] = [
  { value: 'Bug', label: 'Bug / Error', emoji: '🐛' },
  { value: 'FeatureRequest', label: 'Feature Request', emoji: '✨' },
  { value: 'Question', label: 'Question', emoji: '❓' },
  { value: 'Improvement', label: 'Improvement', emoji: '⚡' },
  { value: 'Other', label: 'Other', emoji: '💬' },
]

const PRIORITIES: { value: SupportTicketPriority; label: string; color: string }[] = [
  { value: 'Low', label: 'Low', color: '#6b7280' },
  { value: 'Medium', label: 'Medium', color: '#f59e0b' },
  { value: 'High', label: 'High', color: '#ef4444' },
  { value: 'Critical', label: 'Critical', color: '#7c3aed' },
]

export default function SupportTicketModal({ open, onClose }: SupportTicketModalProps) {
  const queryClient = useQueryClient()
  const [submitted, setSubmitted] = useState(false)
  const [ticketNumber, setTicketNumber] = useState('')
  const [form, setForm] = useState({
    category: 'Bug' as SupportTicketCategory,
    priority: 'Medium' as SupportTicketPriority,
    title: '',
    description: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  const mutation = useMutation({
    mutationFn: submitSupportTicket,
    onSuccess: (ticket) => {
      setTicketNumber(ticket.ticketNumber)
      setSubmitted(true)
      queryClient.invalidateQueries({ queryKey: ['support-tickets'] })
    },
  })

  const validate = () => {
    const errs: Record<string, string> = {}
    if (!form.title.trim()) errs.title = 'Title is required.'
    else if (form.title.length > 200) errs.title = 'Title must be 200 characters or less.'
    if (!form.description.trim()) errs.description = 'Description is required.'
    else if (form.description.length > 4000) errs.description = 'Description must be 4000 characters or less.'
    setErrors(errs)
    return Object.keys(errs).length === 0
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    if (!validate()) return
    mutation.mutate(form)
  }

  const handleClose = () => {
    if (mutation.isPending) return
    setSubmitted(false)
    setTicketNumber('')
    setForm({ category: 'Bug', priority: 'Medium', title: '', description: '' })
    setErrors({})
    mutation.reset()
    onClose()
  }

  if (!open) return null

  return (
    <div className="stm-overlay" role="dialog" aria-modal="true" aria-label="Support ticket">
      <div className="stm-modal">
        <div className="stm-header">
          <div className="stm-header-title">
            <LifeBuoy size={20} />
            <span>Report an Issue / Request Support</span>
          </div>
          <button className="stm-close" onClick={handleClose} aria-label="Close" disabled={mutation.isPending}>
            <X size={18} />
          </button>
        </div>

        {submitted ? (
          <div className="stm-success">
            <CheckCircle size={48} className="stm-success-icon" />
            <h3>Ticket Submitted</h3>
            <p className="stm-ticket-num">{ticketNumber}</p>
            <p>Your request has been logged. The support team will review it shortly.</p>
            <button className="stm-btn stm-btn-primary" onClick={handleClose}>
              Close
            </button>
          </div>
        ) : (
          <form className="stm-form" onSubmit={handleSubmit} noValidate>
            {/* Category */}
            <div className="stm-field">
              <label className="stm-label">Category</label>
              <div className="stm-category-grid">
                {CATEGORIES.map((cat) => (
                  <button
                    key={cat.value}
                    type="button"
                    className={`stm-category-btn ${form.category === cat.value ? 'selected' : ''}`}
                    onClick={() => setForm((f) => ({ ...f, category: cat.value }))}
                  >
                    <span className="stm-cat-emoji">{cat.emoji}</span>
                    <span>{cat.label}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Priority */}
            <div className="stm-field">
              <label className="stm-label">Priority</label>
              <div className="stm-priority-row">
                {PRIORITIES.map((p) => (
                  <button
                    key={p.value}
                    type="button"
                    className={`stm-priority-btn ${form.priority === p.value ? 'selected' : ''}`}
                    style={form.priority === p.value ? { borderColor: p.color, color: p.color } : {}}
                    onClick={() => setForm((f) => ({ ...f, priority: p.value }))}
                  >
                    {p.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Title */}
            <div className="stm-field">
              <label className="stm-label" htmlFor="stm-title">
                Title <span className="stm-required">*</span>
              </label>
              <input
                id="stm-title"
                className={`stm-input ${errors.title ? 'stm-input-error' : ''}`}
                type="text"
                placeholder="One-line summary of the issue"
                maxLength={200}
                value={form.title}
                onChange={(e) => {
                  setForm((f) => ({ ...f, title: e.target.value }))
                  if (errors.title) setErrors((e) => ({ ...e, title: '' }))
                }}
              />
              {errors.title && <span className="stm-error-msg">{errors.title}</span>}
            </div>

            {/* Description */}
            <div className="stm-field">
              <label className="stm-label" htmlFor="stm-desc">
                Description <span className="stm-required">*</span>
              </label>
              <textarea
                id="stm-desc"
                className={`stm-textarea ${errors.description ? 'stm-input-error' : ''}`}
                placeholder="Describe the issue in detail. Include steps to reproduce if it's a bug."
                maxLength={4000}
                rows={5}
                value={form.description}
                onChange={(e) => {
                  setForm((f) => ({ ...f, description: e.target.value }))
                  if (errors.description) setErrors((e) => ({ ...e, description: '' }))
                }}
              />
              <span className="stm-char-count">{form.description.length} / 4000</span>
              {errors.description && <span className="stm-error-msg">{errors.description}</span>}
            </div>

            {mutation.isError && (
              <div className="stm-api-error">
                Failed to submit ticket. Please try again.
              </div>
            )}

            <div className="stm-footer">
              <button type="button" className="stm-btn stm-btn-ghost" onClick={handleClose} disabled={mutation.isPending}>
                Cancel
              </button>
              <button type="submit" className="stm-btn stm-btn-primary" disabled={mutation.isPending}>
                {mutation.isPending ? (
                  'Submitting…'
                ) : (
                  <>
                    <Send size={15} />
                    Submit Ticket
                  </>
                )}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  )
}
